/**
 * Static action registry — single source of truth for all domain actions.
 *
 * Each resource is defined with `resource(name, displayName)`, optionally
 * chained with `.childOf(parent)`, and completed with `.addAction({...})`.
 * Parents must be defined before their children.
 *
 * Adding a new action:
 *   1. Find the resource (or create one with `resource()`/`.childOf()`).
 *   2. Add the action key to the resource's `.addAction()` object literal:
 *        const myResource = resource('my-resource', 'My Resource')
 *          .childOf(parent)
 *          .addAction({
 *            DoThing: { description: '...', permission: true, audit: true },
 *            ClickThing: { description: 'Click a thing', tracking: true },
 *          });
 *   3. The registry auto-generates typed exports:
 *      - Permission.MyResourceDoThing  ('my-resource:do-thing')
 *      - AuditEventType.MyResourceDoThing  (audit string)
 *      - TrackingEvent.MyResourceClickThing (description string)
 */

import {
  resource,
  buildRegistry,
  type ActionDef,
  type ActionEntry,
  type RegistryOutput,
} from './action-registry-builder.js';

// Re-export shared types for backward compat
export type { ActionDef, ActionEntry, RegistryOutput };

// ===== Resources =====

const root = resource('root', 'Root').addAction({});

const matter = resource('matter', 'Matters')
  .childOf(root)
  .addAction({
    AccessAll: {
      description: 'View all Matters in your organisation - not only assigned ones',
      permission: true,
      matterScoped: false,
    },
    ViewUsers: {
      description: 'View all users within a Matter',
      permission: true,
    },
    Create: {
      description: 'Create a new Matter, and edit or delete Matters you have created',
      permission: true,
      matterScoped: false,
      audit: true,
      tracking: true,
      trackingName: 'Create Matter',
    },
    CreateSample: {
      description: 'Create a sample Matter',
      tracking: true,
    },
    Edit: {
      description: 'Edit any Matter you have access to',
      permission: true,
      audit: true,
      tracking: true,
      trackingName: 'Edit Matter',
    },
    Delete: {
      description: 'Delete any Matter you have access to',
      permission: true,
      audit: true,
    },
    AddUsers: {
      description: 'Add users to a Matter',
      permission: true,
      audit: true,
    },
    RemoveUsers: {
      description: 'Remove users from a Matter',
      permission: true,
      audit: true,
    },
    TogglePermissionOverrides: {
      description: 'Toggle Matter-level permission overrides on or off',
      audit: true,
    },
    SetPermissionOverride: {
      description: 'Create or update a permission override for a user on a Matter',
      audit: true,
    },
    RemovePermissionOverride: {
      description: 'Remove a permission override for a user on a Matter',
      audit: true,
    },
    ResetPermissionOverrides: {
      description: 'Remove all permission overrides on a Matter',
      audit: true,
    },
    AddIssue: {
      description: 'Add an issue to a Matter',
      audit: true,
    },
    RemoveIssue: {
      description: 'Remove an issue from a Matter',
      audit: true,
    },
    AddParty: {
      description: 'Add a party to a Matter',
      audit: true,
    },
    RemoveParty: {
      description: 'Remove a party from a Matter',
      audit: true,
    },
    UpdateAdditionalContext: {
      description: 'Update additional context on a Matter',
      audit: true,
    },
    GenerateDetails: {
      description: 'Generate Matter details with AI',
      tracking: true,
    },
    AddTeamMemberOnCreator: {
      description: 'Add team member during Matter creation',
      tracking: true,
    },
    RemoveTeamMemberOnCreator: {
      description: 'Remove team member during Matter creation',
      tracking: true,
      trackingName: 'Remove team member on creator',
    },
    AddUser: {
      description: 'Add user to a Matter',
      tracking: true,
    },
    AddUserFromMatterPage: {
      description: 'Add user to Matter from Matter page',
      tracking: true,
    },
    RemoveUser: {
      description: 'Remove user from a Matter',
      tracking: true,
    },
    CopyLink: {
      description: 'Copy Matter link',
      tracking: true,
    },
    ViewSettingsFromOverview: {
      description: 'View Matter settings from the overview page',
      tracking: true,
    },
    ViewUsersFromOverview: {
      description: 'View Matter users from the overview page',
      tracking: true,
    },
    ViewIssueFromOverview: {
      description: 'Open a Matter issue from the overview page',
      tracking: true,
    },
    ClickRecentActivity: {
      description: 'Click a recent activity item on the overview page',
      tracking: true,
    },
    ToggleRecentActivity: {
      description: 'Expand or collapse recent activity',
      tracking: true,
    },
    ExpandSummaryFromOverview: {
      description: 'Expand the summary on the overview page',
      tracking: true,
    },
    ExpandIssuesFromOverview: {
      description: 'Expand the issues list on the overview page',
      tracking: true,
    },
    RequestAccess: {
      description: 'Request access to a Matter',
      tracking: true,
    },
    InviteUserNoAccount: {
      description: 'Invite user who has no account',
      tracking: true,
    },
    ContextUpload: {
      description: 'Upload context Documents during Matter creation',
      tracking: true,
    },
    ContextSkipUpload: {
      description: 'Skip uploading context documents during Matter creation',
      tracking: true,
    },
    ContextIssueAdd: {
      description: 'Manually add a Matter issue',
      tracking: true,
    },
    ContextIssueEdit: {
      description: 'Edit a Matter issue',
      tracking: true,
    },
    ContextIssueDelete: {
      description: 'Delete a Matter issue',
      tracking: true,
    },
    ContextPartyAdd: {
      description: 'Manually add a Matter party',
      tracking: true,
    },
    ContextPartyEdit: {
      description: 'Edit a Matter party',
      tracking: true,
    },
    ContextPartyDelete: {
      description: 'Delete a Matter party',
      tracking: true,
    },
    ContextAnswerSuggestedQuestion: {
      description: 'Answer a suggested context question during Matter creation',
      tracking: true,
    },
    IssuesSave: {
      description: 'Save Matter issues from the Matter settings page',
      tracking: true,
    },
    IssuesSubmit: {
      description: 'Submit Matter issues for reprocessing from the Matter settings page',
      tracking: true,
    },
  });

const document = resource('document', 'Documents')
  .childOf(matter)
  .addAction({
    ClickView: {
      description: 'Click to view Document',
      tracking: true,
    },
    Upload: {
      description: 'Upload Documents to a Matter',
      permission: true,
      delegatedBy: [[matter, 'Create']],
      audit: true,
    },
    AudioTranscript: {
      description: 'Audio transcript action',
      tracking: true,
    },
    ClickAdd: {
      description: 'Click add Documents button',
      tracking: true,
    },
    SelectFromListClicked: {
      description: 'Document select from list clicked',
      tracking: true,
    },
    SelectFromListFileSubmitted: {
      description: 'Document select from list file submitted',
      tracking: true,
    },
    RelativityListCreated: {
      description: 'Relativity list successfully created from selected documents',
      tracking: true,
    },
    Update: {
      description: 'Update Document names, dates, and authors',
      permission: true,
    },
    UpdateName: {
      description: 'Update Document name',
      tracking: true,
    },
    UpdateDate: {
      description: 'Update Document date',
      tracking: true,
    },
    UpdateAuthor: {
      description: 'Update Document author',
      tracking: true,
    },
    UpdateType: {
      description: 'Update Document type',
      tracking: true,
      legacyTrackingName: 'UpdateType',
    },
    UpdateIsPleading: {
      description: 'Update Document pleading status',
      tracking: true,
      legacyTrackingName: 'UpdateIsPleading',
    },
    Delete: {
      description: 'Delete Documents from a Matter',
      permission: true,
      audit: true,
      tracking: true,
      trackingName: 'Delete Documents',
    },
    Download: {
      description: 'Download Documents and Document indices',
      permission: true,
      tracking: true,
      trackingName: 'Download Documents',
      audit: true,
    },
    DownloadEmailTemplate: {
      description: 'Download email template',
      tracking: true,
    },
    DownloadOverview: {
      description: 'Download Document overview',
      tracking: true,
    },
    DownloadCaseIssues: {
      description: 'Download case issues sheet',
      tracking: true,
    },
    DownloadPersonsCsv: {
      description: 'Download persons table as CSV',
      tracking: true,
    },
    DownloadPersonsDocx: {
      description: 'Download persons table as DOCX',
      tracking: true,
    },
    DownloadPersonsWritten: {
      description: 'Download persons written report',
      tracking: true,
    },
    ApplyFilters: {
      description: 'Apply filters on Documents page',
      tracking: true,
    },
    ApplySort: {
      description: 'Apply sort on Documents page',
      tracking: true,
    },
  });

const subset = resource('subset', 'Subsets')
  .childOf(matter)
  .addAction({
    View: {
      description: 'View Subsets',
      tracking: true,
    },
    ViewFactsFromSummary: {
      description: 'View Facts from Subset summary',
      tracking: true,
    },
    ViewDocumentsFromSummary: {
      description: 'View Documents from Subset summary',
      tracking: true,
    },
    ViewKeyDocumentsFromSummary: {
      description: 'View key Documents from Subset summary',
      tracking: true,
    },
    Create: {
      description: 'Create a Subset, and edit or delete Subsets you have created',
      permission: true,
      audit: true,
      tracking: true,
      trackingName: 'Create Subset',
    },
    OpenCreateNew: {
      description: 'Open create new Subset dialog',
      tracking: true,
    },
    Edit: {
      description: 'Edit any Subset you have access to',
      permission: true,
      audit: true,
      tracking: true,
      trackingName: 'Edit Subset',
    },
    EditFromSummary: {
      description: 'Edit existing Subset from summary page',
      tracking: true,
    },
    Delete: {
      description: 'Delete any Subset you have access to',
      permission: true,
      audit: true,
    },
    CopyMatterIssues: {
      description: 'Copy Matter issues to Subset',
      tracking: true,
    },
    AddFilter: {
      description: 'Add filter to Subset',
      tracking: true,
    },
    ShowSelectedDocuments: {
      description: 'Click show selected Documents',
      tracking: true,
    },
    ResetSelection: {
      description: 'Click reset selection',
      tracking: true,
    },
    AddIssue: {
      description: 'Add an issue to a Subset',
      audit: true,
    },
    RemoveIssue: {
      description: 'Remove an issue from a Subset',
      audit: true,
    },
  });

const fact = resource('fact', 'Facts')
  .childOf(subset)
  .addAction({
    Create: {
      description: 'Manually create a Fact',
      permission: true,
      audit: true,
    },
    CreateFromFactsPage: {
      description: 'Create Fact from the Facts page',
      tracking: true,
    },
    CreateFromDocViewer: {
      description: 'Create Fact from Document viewer',
      tracking: true,
    },
    AddOnFactsPage: {
      description: 'Add Fact on Facts page',
      tracking: true,
    },
    ConfirmCreateFromImage: {
      description: 'Confirm create Fact from image selection',
      tracking: true,
    },
    SelectCreateFromImage: {
      description: 'Select to create Fact from image selection',
      tracking: true,
      trackingName: 'Select create Fact from image',
    },
    Update: {
      description: 'Update Fact descriptions, relevance, dates, and classifications',
      permission: true,
      audit: true,
    },
    UpdateContent: {
      description: 'Update Fact content',
      tracking: true,
    },
    UpdateRelevance: {
      description: 'Update Fact relevance',
      tracking: true,
    },
    UpdateDate: {
      description: 'Update Fact date',
      tracking: true,
    },
    BulkUpdate: {
      description: 'Bulk update Facts',
      audit: true,
      tracking: true,
    },
    SetKey: {
      description: 'Set key for Facts',
      tracking: true,
    },
    Delete: {
      description: 'Delete or archive a Fact',
      permission: true,
      audit: true,
    },
    DeleteFact: {
      description: 'Delete a Fact',
      tracking: true,
    },
    BulkDelete: {
      description: 'Bulk delete Facts',
      audit: true,
      tracking: true,
    },
    CommentAdded: {
      description: 'Comment added to Fact',
      tracking: true,
    },
    CopyId: {
      description: 'Copy Fact ID',
      tracking: true,
    },
    ToggleAllOn: {
      description: 'Toggle all Facts on',
      tracking: true,
    },
    ToggleAllOff: {
      description: 'Toggle all Facts off',
      tracking: true,
    },
    Search: {
      description: 'Search Facts',
      tracking: true,
    },
    ToggleKey: {
      description: 'Toggle key Facts filter',
      tracking: true,
    },
    ToggleDisputed: {
      description: 'Toggle disputed Facts filter',
      tracking: true,
    },
    FilterByDate: {
      description: 'Filter Facts by date',
      tracking: true,
    },
    FilterByDocument: {
      description: 'Filter Facts by Document',
      tracking: true,
    },
    FilterByRelevance: {
      description: 'Filter Facts by relevance',
      tracking: true,
    },
    FilterByClassification: {
      description: 'Filter Facts by classification',
      tracking: true,
    },
    FilterByProcessingRun: {
      description: 'Filter Facts by processing run',
      tracking: true,
    },
    FilterByDocumentType: {
      description: 'Filter Facts by Document type',
      tracking: true,
    },
    FilterByTheme: {
      description: 'Filter Facts by theme',
      tracking: true,
    },
    FilterByPerson: {
      description: 'Filter Facts by person',
      tracking: true,
    },
    Download: {
      description: 'Download the Facts table',
      permission: true,
      audit: true,
    },
    DownloadCsv: {
      description: 'Download Facts table as CSV',
      tracking: true,
    },
    DownloadDocx: {
      description: 'Download Facts table as DOCX',
      tracking: true,
    },
    DownloadWithDocuments: {
      description: 'Download Facts with source Documents',
      tracking: true,
    },
  });

const chronology = resource('chronology', 'Chronologies')
  .childOf(subset)
  .addAction({
    View: {
      description: 'View a chronology',
      tracking: true,
    },
    Create: {
      description: 'Create a chronology, and edit or delete chronologies you have created',
      permission: true,
      audit: true,
    },
    CreateFromFactsPage: {
      description: 'Create Chronology from Facts page',
      tracking: true,
    },
    Edit: {
      description: 'Edit any chronology',
      permission: true,
      audit: true,
    },
    UpdateName: {
      description: 'Update Chronology name',
      tracking: true,
    },
    UpdateFactDate: {
      description: 'Update Fact date in chronology',
      tracking: true,
    },
    AddFact: {
      description: 'Add a Fact to a chronology',
      audit: true,
    },
    AddFactOnVisualPage: {
      description: 'Add Fact from table on visual chronology page',
      tracking: true,
      trackingName: 'Add Fact on visual Chronology',
    },
    AddFromFactsPage: {
      description: 'Add to Chronology from Facts page',
      tracking: true,
    },
    VerifyFact: {
      description: 'Verify a Fact in a chronology',
      audit: true,
    },
    VerifyFactTracking: {
      description: 'Verify a chronology Fact',
      tracking: true,
    },
    UnverifyFact: {
      description: 'Unverify a chronology Fact',
      tracking: true,
    },
    Delete: {
      description: 'Delete any chronology',
      permission: true,
      audit: true,
      tracking: true,
    },
    RemoveFact: {
      description: 'Remove a Fact from chronology',
      audit: true,
      tracking: true,
    },
    Download: {
      description: 'Download a chronology',
      permission: true,
      audit: true,
    },
    DownloadCsv: {
      description: 'Download chronology table as CSV',
      tracking: true,
    },
    DownloadDocx: {
      description: 'Download chronology table as DOCX',
      tracking: true,
    },
    DownloadWrittenDocx: {
      description: 'Download chronology written as DOCX',
      tracking: true,
    },
    DownloadWrittenCustom: {
      description: 'Download chronology with custom style',
      tracking: true,
    },
    DownloadWithDocuments: {
      description: 'Download Chronology with Documents',
      tracking: true,
    },
    AddVisualNode: {
      description: 'Add visual Chronology node',
      tracking: true,
    },
    EditVisualNode: {
      description: 'Edit visual Chronology node',
      tracking: true,
    },
    DeleteVisualNode: {
      description: 'Delete visual Chronology node',
      tracking: true,
    },
    SaveVisual: {
      description: 'Save visual Chronology',
      tracking: true,
    },
    ExportVisual: {
      description: 'Export visual Chronology',
      tracking: true,
    },
    SearchVisual: {
      description: 'Search visual Chronology',
      tracking: true,
    },
    SwitchToVisual: {
      description: 'Switch to visual chronology view',
      tracking: true,
    },
  });

const docChronology = resource('docChronology', 'Document Chronologies')
  .childOf(subset)
  .addAction({
    Create: {
      description:
        'Create a document chronology, and edit or delete document chronologies you have created',
      permission: true,
      audit: true,
      tracking: true,
      trackingName: 'Create Document Chronology',
    },
    Edit: {
      description: 'Edit any Document chronology',
      permission: true,
      audit: true,
    },
    UpdateDoc: {
      description: 'Update a Document within a Document chronology',
      audit: true,
    },
    AddFact: {
      description: 'Add a Fact to a doc chronology Document',
      audit: true,
    },
    RemoveFact: {
      description: 'Remove a Fact from a doc chronology Document',
      audit: true,
    },
    AddFactToChronology: {
      description: 'Add a Fact to a doc chronology',
      audit: true,
    },
    AddDocument: {
      description: 'Add a Document to a Document chronology',
      audit: true,
    },
    RemoveDocument: {
      description: 'Remove a Document from a Document chronology',
      audit: true,
    },
    Delete: {
      description: 'Delete any Document chronology',
      permission: true,
      audit: true,
    },
    AddDocs: {
      description: 'Add docs to Document chronology',
      audit: true,
      tracking: true,
    },
    Download: {
      description: 'Download any Document chronology',
      permission: true,
      tracking: true,
      trackingName: 'Download Document chronology',
      audit: true,
    },
  });

// CommentAdd allows creating comments. Update remains an audit event for
// author-owned edits, while CommentDelete grants moderation over all comments.
const comment = resource('comment', 'Comments')
  .childOf(subset)
  .addAction({
    Add: {
      description: 'Add comments on Facts',
      permission: true,
      audit: true,
    },
    Update: { description: 'Update a comment', audit: true },
    Delete: {
      description: 'Delete any comments on Facts',
      permission: true,
      audit: true,
    },
  });

const thread = resource('thread', 'Kim (AI Assistant)')
  .childOf(subset)
  .addAction({
    Create: {
      description: 'Create threads and ask questions using Kim AI assistant',
      permission: true,
      audit: true,
    },
    Publish: {
      description: 'Publish and manage Kim threads',
      permission: true,
    },
    Delete: { description: 'Delete a Kim thread', audit: true }, // audit-only — gated by ThreadCreate
    AskQuestion: {
      description: 'Ask Kim a question',
      tracking: true,
    },
    AskFromDocViewer: {
      description: 'Ask Kim from Document viewer',
      tracking: true,
    },
    ExecutePlan: {
      description: 'Execute Kim plan',
      tracking: true,
    },
    ReferenceClicked: {
      description: 'Click Kim reference',
      tracking: true,
    },
    Download: {
      description: 'Download a Kim answer',
      permission: true,
      tracking: true,
      audit: true,
    },
    CopyAnswer: {
      description: 'Copy Kim answer',
      tracking: true,
    },
    CopyPageLink: {
      description: 'Copy Kim page link',
      tracking: true,
    },
    ConfidenceLevel: {
      description: 'Click Kim confidence level',
      tracking: true,
    },
    ExampleQuestion: {
      description: 'Click Kim example question',
      tracking: true,
    },
    FollowUpQuestion: {
      description: 'Click Kim follow up question',
      tracking: true,
    },
    TagDocument: {
      description: 'Tag a Document in Kim with @',
      tracking: true,
    },
    ClickHelp: {
      description: 'Click Kim help',
      tracking: true,
    },
    OnboardingQuestionFlow: {
      description: 'Click question flow in Kim onboarding',
      tracking: true,
    },
    OnboardingExtractionFlow: {
      description: 'Click extraction flow in Kim onboarding',
      tracking: true,
    },
    OnboardingDraftFlow: {
      description: 'Click draft flow in Kim onboarding',
      tracking: true,
    },
    OnboardingExampleQuestion: {
      description: 'Click example question in Kim onboarding',
      tracking: true,
      trackingName: 'Kim onboarding example question',
    },
    OnboardingCustomQuestion: {
      description: 'Click custom question in Kim onboarding',
      tracking: true,
      trackingName: 'Kim onboarding custom question',
    },
    ThreadPublished: {
      description: 'Publish a Kim thread',
      tracking: true,
    },
    ToggleThinking: {
      description: 'Toggle Kim thinking display',
      tracking: true,
    },
    UpdatePlan: {
      description: 'Update Kim plan',
      tracking: true,
    },
  });

const kim = resource('kim', 'Kim (New)')
  .childOf(subset)
  .addAction({
    CreateThread: {
      description: 'Create a new Kim thread',
      tracking: true,
      legacyTrackingName: 'CreateThread',
      audit: true,
    },
    SendMessage: {
      description: 'Send a message in Kim',
      tracking: true,
      legacyTrackingName: 'SendMessage',
    },
    RenameThread: {
      description: 'Rename a Kim thread',
      tracking: true,
      legacyTrackingName: 'RenameThread',
    },
    DeleteThread: {
      description: 'Delete a Kim thread',
      tracking: true,
      legacyTrackingName: 'DeleteThread',
      audit: true,
    },
    DownloadArtefact: {
      description: 'Download a Kim artefact',
      tracking: true,
      legacyTrackingName: 'DownloadArtefact',
    },
    DownloadAnswer: {
      description: 'Download an answer from new Kim',
      tracking: true,
      legacyTrackingName: 'DownloadAnswer',
    },
    EnableNewExperience: {
      description: 'Enable the new Kim experience',
      tracking: true,
      legacyTrackingName: 'EnableNewExperience',
    },
    ReturnToOldExperience: {
      description: 'Return to the old Kim experience',
      tracking: true,
      legacyTrackingName: 'ReturnToOldExperience',
    },
    DismissNewKimDialog: {
      description: 'Dismiss the new Kim dialog',
      tracking: true,
      legacyTrackingName: 'DismissNewKimDialog',
    },
    CreateTemplate: {
      description: 'Create Kim template',
      tracking: true,
    },
    UseTemplate: {
      description: 'Use Kim template',
      tracking: true,
    },
    OpenRecentThread: {
      description: 'Open a recent Kim thread from the Matter overview',
      tracking: true,
    },
    GenerateTemplateFromMessage: {
      description: 'Generate Kim template from message',
      tracking: true,
    },
    AddFile: {
      description: 'Add file to Kim message',
      tracking: true,
    },
    Interrupt: {
      description: 'Interrupt Kim thread',
      tracking: true,
    },
    EnableNotifications: {
      description: 'Enable email notifications on a Kim thread',
      tracking: true,
    },
    DisableNotifications: {
      description: 'Disable email notifications on a Kim thread',
      tracking: true,
    },
  });

// RunCreate is audit-only — any user on a matter can start a run.
// Only approval requires an explicit permission.
const run = resource('run', 'Processing Runs')
  .childOf(matter)
  .addAction({
    Create: { description: 'Start a processing run', audit: true },
    Reprocess: { description: 'Reprocess failed docs of a run', audit: true },
    Approve: {
      description: 'Admin approval for processing runs',
      permission: true,
      audit: true,
    },
    Reject: {
      description: 'Reject a processing run pending approval',
      audit: true,
    },
    NameOnUpload: {
      description: 'Name a processing run during Document upload',
      tracking: true,
      trackingName: 'Name run on upload',
    },
    NameEdit: {
      description: 'Edit a processing run name from the run log',
      tracking: true,
      trackingName: 'Edit run name',
    },
    ViewUploadBreakdown: {
      description: 'View upload breakdown from the overview run status',
      tracking: true,
    },
  });

const realtime = resource('realtime', 'Realtime')
  .childOf(matter)
  .addAction({
    Use: {
      description: 'Access Wexler Real Time for live transcription & Inconsistency detection',
      permission: true,
      audit: true,
    },
    StartRecording: {
      description: 'Start a recording',
      tracking: true,
    },
    PauseRecording: {
      description: 'Pause a recording',
      tracking: true,
    },
    StopRecording: {
      description: 'Stop a recording',
      tracking: true,
    },
    ClickInfo: {
      description: 'Click transcript info button',
      tracking: true,
    },
    EditBlockText: {
      description: 'Edit transcript block text',
      tracking: true,
    },
    EditBlockSpeaker: {
      description: 'Edit transcript block speaker',
      tracking: true,
    },
    AssignSpeaker: {
      description: 'Assign speaker in transcript',
      tracking: true,
    },
    ViewSummary: {
      description: 'View transcript summary',
      tracking: true,
    },
    ClickInconsistencySeeMore: {
      description: 'Click Inconsistency see more',
      tracking: true,
    },
    ClickInconsistencyReference: {
      description: 'Click Inconsistency reference',
      tracking: true,
    },
    ClickInconsistencyReferenceBlock: {
      description: 'Click Inconsistency reference block',
      tracking: true,
    },
    ClickDiarizationCorrection: {
      description: 'Click diarization correction',
      tracking: true,
    },
    AcceptDiarizationCorrection: {
      description: 'Accept diarization correction',
      tracking: true,
    },
    RejectDiarizationCorrection: {
      description: 'Reject diarization correction',
      tracking: true,
    },
    ClickNewRecording: {
      description: 'Click new recording button',
      tracking: true,
    },
    DeleteTranscript: {
      description: 'Delete a transcript',
      tracking: true,
    },
  });

// Kept for audit trail — old events reference this resource and must still render on the frontend.
const inconsistencyReport = resource('inconsistencyReport', 'Inconsistency Reports')
  .childOf(subset)
  .addAction({
    Create: {
      description: 'Create an Inconsistency report',
      audit: true,
    },
    Delete: {
      description: 'Delete an Inconsistency report',
      audit: true,
    },
  });

const user = resource('user', 'Users')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Update and assign user roles, and manage Matter or platform access',
      permission: true,
    },
  });

const organisation = resource('organisation', 'Organisation')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Manage organisation-wide settings',
      permission: true,
    },
  });

const integrations = resource('integrations', 'Integrations')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Configure and manage external system integrations',
      permission: true,
    },
    UploadAttempted: {
      description: 'Integration upload attempted',
      tracking: true,
    },
    UploadSuccessful: {
      description: 'Integration upload successful',
      tracking: true,
    },
    UploadUnsuccessful: {
      description: 'Integration upload unsuccessful',
      tracking: true,
    },
    Selected: {
      description: 'Integration selected',
      tracking: true,
    },
  });

const tokenLimits = resource('token-limits', 'Credit Approval Limits')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Manage credit approval limits',
      permission: true,
    },
  });

const promptTemplate = resource('prompt-template', 'Templates')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Create, edit and delete organisation-wide templates',
      permission: true,
    },
  });

const relativityNode = resource('relativity', 'Relativity')
  .childOf(root)
  .addAction({
    Import: {
      description: 'Import Documents and data from Relativity',
      permission: true,
    },
    Sync: {
      description: 'Sync Relativity metadata for Matter documents',
      audit: true,
      tracking: true,
      trackingName: 'Relativity Sync',
    },
    BuildParentChildRelationships: {
      description: 'Build Relativity parent/child document relationships',
      audit: true,
      tracking: true,
      trackingName: 'Relativity Parent Child Sync',
    },
    LoadFileImport: {
      description: 'Import Relativity metadata from a load file (e.g. .dat)',
      audit: true,
      tracking: true,
      trackingName: 'Relativity Load File Import',
    },
  });

const usageDashboard = resource('usage-dashboard', 'Usage Dashboard')
  .childOf(root)
  .addAction({
    View: {
      description: 'Access organisation usage analytics and reports',
      permission: true,
    },
    DownloadData: {
      description: 'Download usage data',
      tracking: true,
    },
    DownloadCreditBreakdown: {
      description: 'Download credit usage by matter',
      tracking: true,
    },
  });

const audit = resource('audit', 'Audit')
  .childOf(root)
  .addAction({
    View: {
      description: 'Access detailed audit trails of accessible Matters',
      permission: true,
    },
  });

const docViewer = resource('doc-viewer', 'Document Viewer')
  .childOf(root)
  .addAction({
    OpenFromFact: {
      description: 'Open Document viewer from a Fact',
      tracking: true,
    },
    OpenFromKim: {
      description: 'Open Document viewer from Kim',
      tracking: true,
    },
    OpenFromChronology: {
      description: 'Open Document viewer from chronology',
      tracking: true,
    },
    OpenFromVisualChronology: {
      description: 'Open Document viewer from visual chronology',
      tracking: true,
      trackingName: 'Open Doc viewer from visual Chronology',
    },
    ShowAllFacts: {
      description: 'Show all Facts in Document viewer',
      tracking: true,
    },
    DownloadDocument: {
      description: 'Download Document in Document viewer',
      tracking: true,
    },
    DownloadAudioTranscript: {
      description: 'Download audio transcript as PDF from Document viewer',
      tracking: true,
    },
    JumpToFact: {
      description: 'Jump to Fact in Document from doc Facts list',
      tracking: true,
      trackingName: 'Jump to Fact in Doc viewer',
    },
    Search: {
      description: 'Search within a Document in the Document viewer',
      tracking: true,
      trackingName: 'Search in Doc viewer',
    },
  });

const navigation = resource('navigation', 'Navigation')
  .childOf(root)
  .addAction({
    ClickMatterCard: {
      description: 'Click Matter card on homepage',
      tracking: true,
    },
    ClickRecentSubset: {
      description: 'Click recent Subset on homepage',
      tracking: true,
    },
    ClickBreadcrumbMatter: {
      description: 'Jump to a Matter via the breadcrumb dropdown',
      tracking: true,
    },
    ClickBreadcrumbSubset: {
      description: 'Jump to a Subset via the breadcrumb dropdown',
      tracking: true,
    },
  });

const fileHistory = resource('file-history', 'File History')
  .childOf(root)
  .addAction({
    TabChanged: {
      description: 'File history tab changed',
      tracking: true,
    },
    DownloadOverviewReport: {
      description: 'Download file history overview report',
      tracking: true,
    },
    ViewSkippedBreakdown: {
      description: 'View skipped files breakdown',
      tracking: true,
    },
    ViewDeletedBreakdown: {
      description: 'View deleted files breakdown',
      tracking: true,
    },
    ClickUploadEvent: {
      description: 'Click upload event in file history',
      tracking: true,
    },
    ClickDeletionEvent: {
      description: 'Click deletion event in file history',
      tracking: true,
    },
    OpenSkippedFilesModal: {
      description: 'Open skipped files modal',
      tracking: true,
    },
    OpenDeletedFilesModal: {
      description: 'Open deleted files modal',
      tracking: true,
    },
    DownloadSkippedReport: {
      description: 'Download skipped files report',
      tracking: true,
    },
    DownloadDeletedReport: {
      description: 'Download deleted files report',
      tracking: true,
    },
    OpenSkippedExplainer: {
      description: 'Open skipped files explainer',
      tracking: true,
    },
    OpenUploadExplainer: {
      description: 'Open upload explainer',
      tracking: true,
    },
    DownloadUploadBatchSkippedReport: {
      description: 'Download upload batch skipped report',
      tracking: true,
    },
    DownloadDeletionBatchReport: {
      description: 'Download deletion detail report',
      tracking: true,
    },
    ClickFailedPages: {
      description: 'Click failed pages in file history',
      tracking: true,
    },
    ClickRunRow: {
      description: 'Click a run row in the run history to open its details modal',
      tracking: true,
    },
    OpenBreakdownLink: {
      description: 'Open breakdown link in file history',
      tracking: true,
    },
    OpenBreakdownModal: {
      description: 'Open breakdown modal in file history',
      tracking: true,
    },
  });

const pwa = resource('pwa', 'PWA')
  .childOf(root)
  .addAction({
    PromptInstall: {
      description: 'PWA install prompt clicked',
      tracking: true,
    },
    PromptDismissed: {
      description: 'PWA install prompt dismissed',
      tracking: true,
    },
  });

const inconsistency = resource('inconsistency', 'Inconsistencies')
  .childOf(matter)
  .addAction({
    Edit: {
      description: 'Edit an Inconsistency',
      permission: true,
      audit: true,
      tracking: true,
    },
    Delete: {
      description: 'Delete an Inconsistency',
      permission: true,
      audit: true,
      tracking: true,
    },
    Download: {
      description: 'Download Inconsistencies',
      permission: true,
      tracking: true,
      audit: true,
    },
    Search: {
      description: 'Search Inconsistencies',
      tracking: true,
    },
    FilterByDocument: {
      description: 'Filter Inconsistencies by Document',
      tracking: true,
    },
    FilterBySeverity: {
      description: 'Filter Inconsistencies by severity',
      tracking: true,
    },
    FilterBySource: {
      description: 'Filter Inconsistencies by source',
      tracking: true,
    },
    FilterByType: {
      description: 'Filter Inconsistencies by type',
      tracking: true,
    },
    OpenViewer: {
      description: 'Open Inconsistency viewer',
      tracking: true,
    },
  });

// Gated at the org level by OrgPermission.ProceduralTimelines. Resource lives
// here so the permission still exists for role assignment even when the org
// flag is off; route-level @RequireOrgPermissions enforces the feature gate.
const proceduralTimeline = resource('procedural-timeline', 'Procedural Timelines')
  .childOf(matter)
  .addAction({
    Edit: {
      description:
        'Create, edit, and delete procedural timeline events (requires the Procedural Timelines feature to be enabled for the organisation)',
      permission: true,
      audit: true,
    },
    // Granular audit + tracking actions below.
    FileUpload: {
      description: 'Upload a procedural-timeline source file',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline File Upload',
    },
    SuggestionAccept: {
      description: 'Accept a procedural-timeline suggestion',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline Suggestion Accept',
    },
    SuggestionReject: {
      description: 'Reject a procedural-timeline suggestion',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline Suggestion Reject',
    },
    EventCreate: {
      description: 'Manually create a procedural-timeline event',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline Event Create',
    },
    EventUpdate: {
      description: 'Manually update a procedural-timeline event',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline Event Update',
    },
    EventDelete: {
      description: 'Manually delete a procedural-timeline event',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline Event Delete',
    },
    ViewFromOverview: {
      description: 'Open the procedural timeline from the Matter overview preview',
      tracking: true,
    },
    CalendarDownload: {
      description: 'Download procedural-timeline events as calendar files',
      audit: true,
      tracking: true,
      trackingName: 'Procedural Timeline Calendar Download',
    },
  });

const person = resource('person', 'Entities')
  .childOf(matter)
  .addAction({
    FilterByType: {
      description: 'Filter person by type',
      tracking: true,
    },
    ClickKeyFromHome: {
      description: 'Click key Entity from home',
      tracking: true,
    },
    ClickTab: {
      description: 'Click Entities tab',
      tracking: true,
    },
    ViewAllOnSummary: {
      description: 'View all Entities on summary',
      tracking: true,
    },
    Download: {
      description: 'Download Entities',
      permission: true,
      audit: true,
    },
    Update: {
      description: 'Edit entity name',
      permission: true,
      tracking: true,
      audit: true,
    },
    Delete: {
      description: 'Delete an entity',
      permission: true,
      tracking: true,
      audit: true,
    },
  });

const clientMaterials = resource('client-materials', 'Client Materials')
  .childOf(root)
  .addAction({
    CopyEmailTemplate: {
      description: 'Copy email template',
      tracking: true,
    },
    GenerateCaseDetails: {
      description: 'Generate case details',
      tracking: true,
    },
  });

const feedback = resource('feedback', 'Feedback')
  .childOf(root)
  .addAction({
    Submit: {
      description: 'Submit in-app feedback',
      tracking: true,
    },
  });

const helpCentre = resource('help-centre', 'Help Centre')
  .childOf(root)
  .addAction({
    Search: {
      description: 'Search the help centre',
      tracking: true,
    },
    ViewArticle: {
      description: 'Open a help centre article',
      tracking: true,
    },
  });

// ===== Build registry =====

const registry = buildRegistry(
  root,
  matter,
  document,
  subset,
  fact,
  chronology,
  docChronology,
  comment,
  thread,
  kim,
  run,
  realtime,
  inconsistencyReport,
  user,
  organisation,
  integrations,
  tokenLimits,
  promptTemplate,
  relativityNode,
  usageDashboard,
  audit,
  docViewer,
  navigation,
  fileHistory,
  pwa,
  inconsistency,
  proceduralTimeline,
  person,
  clientMaterials,
  feedback,
  helpCentre,
);

// ===== Typed exports =====

export const REGISTRY = registry;

export const Permission = registry.permissions;
export type Permission = (typeof Permission)[keyof typeof Permission];

export const TrackingEvent = registry.trackingEvents;
export type TrackingEvent = (typeof TrackingEvent)[keyof typeof TrackingEvent];

export const AuditEventType = registry.auditEvents;
export type AuditEventType = (typeof AuditEventType)[keyof typeof AuditEventType];

/** Permissions that can be overridden at the matter level via role overrides. */
export const MATTER_SCOPED_PERMISSIONS = registry.matterScopedPermissions as Set<Permission>;
